import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import useToast from "../../components/common/useToast";
import apiFunctions from "../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler, formatDateTime } from "../../utils/HelperFunctions";
import "../../assets/css/datatable.css";
import BazarProduct from "../../models/bazarProduct";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Skeleton } from "primereact/skeleton";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { BellIcon, RefreshCw, Users } from "lucide-react";
import { UserIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import Chart from "chart.js/auto";
import WhatsAppTemplateSection from "../../components/common/WhatsAppTemplateSection";
import NotificationLinkPicker from "../../components/common/NotificationLinkPicker";
import WhatsAppIcon from "../../components/common/WhatsAppIcon";
import ConfirmActionPopup from "../../components/modal-popup/confirm-action-popup";
import LocationMap, { LocationPoint } from "../../components/common/LocationMap";

const ActiveUsersPage: React.FC = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const { screen } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [newLeadsData, setNewLeadsData] = useState<any[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const [load, setLoad] = useState(true);
    const initialized = useRef(false);
    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [skipData, setSkipData] = useState(0);
    const [totalRecords, settotalRecords] = useState(0);
    const [totalPages, settotalPages] = useState<any>(0);
    const { showToast }: any = useToast();
    const [name, setName] = useState<string>("");
    const [activeAgo, setActiveAgo] = useState<string>("");
    const [sortField, setSortField] = useState<string>("lastActiveAgo");
    const [sortOrder, setSortOrder] = useState<any>(1);

    const activeAgoOptions = [
        { label: "All Time", value: "" },
        { label: "Last 1 Hour", value: "1h" },
        { label: "Last 24 Hours", value: "24h" },
        { label: "Last 7 Days", value: "7d" },
        { label: "Last 30 Days", value: "30d" },
    ];

    const [cityChartData, setCityChartData] = useState<{ city: string; total: number; active: number }[]>([]);
    const cityCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const cityChartRef = useRef<Chart | null>(null);

    const [hourlyChartData, setHourlyChartData] = useState<{ hour: number; count: number }[]>([]);
    const hourlyCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const hourlyChartRef = useRef<Chart | null>(null);

    const [activeLocations, setActiveLocations] = useState<LocationPoint[]>([]);
    const [totalActiveLocationUsers, setTotalActiveLocationUsers] = useState(0);
    const [activeLocationsLoading, setActiveLocationsLoading] = useState(true);
    const [locationRange, setLocationRange] = useState<"today" | "30min">("today");
    const locationRangeOptions = [
        { label: "Today", value: "today" },
        { label: "Last 30 min", value: "30min" },
    ];

    // Map marker click → drills the side panel into a paginated, searchable
    // user list for that city (same pattern as the Dashboard's Registered
    // Users by Location map).
    const CITY_USERS_LIMIT = 20;
    const [selectedActiveCity, setSelectedActiveCity] = useState<string | null>(null);
    const [activeCityUsers, setActiveCityUsers] = useState<any[]>([]);
    const [activeCityUsersTotal, setActiveCityUsersTotal] = useState(0);
    const [activeCityUsersPage, setActiveCityUsersPage] = useState(0);
    const [activeCityUsersLoading, setActiveCityUsersLoading] = useState(false);
    const [activeCitySearch, setActiveCitySearch] = useState("");

    const fetchActiveCityUsers = async (city: string, page: number, reset: boolean, search: string) => {
        if (activeCityUsersLoading) return;
        setActiveCityUsersLoading(true);
        try {
            const params = new URLSearchParams({ city, range: locationRange, page: String(page), limit: String(CITY_USERS_LIMIT) });
            if (screen) params.set("screen", screen);
            if (search.trim()) params.set("search", search.trim());
            const response = await apiFunctions.get(`/activeusers/locations/users?${params.toString()}`);
            if (response?.data?.success) {
                const newItems = response.data.data || [];
                setActiveCityUsers((prev) => (reset ? newItems : [...prev, ...newItems]));
                setActiveCityUsersTotal(response.data.totalRecords || 0);
                setActiveCityUsersPage(page + 1);
            }
        } catch (error) {
            console.error("Failed to load active city users:", error);
        } finally {
            setActiveCityUsersLoading(false);
        }
    };

    const handleActiveCityClick = (city: string) => {
        setSelectedActiveCity(city);
        setActiveCityUsers([]);
        setActiveCityUsersPage(0);
        setActiveCitySearch("");
        fetchActiveCityUsers(city, 0, true, "");
    };

    const handleActiveCitySearchChange = (value: string) => {
        setActiveCitySearch(value);
        setActiveCityUsers([]);
        setActiveCityUsersPage(0);
        if (selectedActiveCity) fetchActiveCityUsers(selectedActiveCity, 0, true, value);
    };

    const handleActiveCityUsersScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.currentTarget;
        const nearBottom = scrollTop + offsetHeight >= scrollHeight - 10;
        const moreToLoad = activeCityUsers.length < activeCityUsersTotal;
        if (nearBottom && moreToLoad && !activeCityUsersLoading && selectedActiveCity) {
            fetchActiveCityUsers(selectedActiveCity, activeCityUsersPage, false, activeCitySearch);
        }
    };

    // Range changed while a city is drilled in → refetch that city with the
    // new range instead of showing stale-range results.
    useEffect(() => {
        if (selectedActiveCity) {
            setActiveCityUsers([]);
            setActiveCityUsersPage(0);
            fetchActiveCityUsers(selectedActiveCity, 0, true, activeCitySearch);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationRange]);

    const activeCitySidebarContent = selectedActiveCity ? (
        <div className="flex flex-col" style={{ height: "420px" }}>
            <div
                className="flex items-center gap-2 cursor-pointer"
                style={{ flexShrink: 0, padding: "0 0 10px", borderBottom: "1px solid #e5e7eb" }}
                onClick={() => setSelectedActiveCity(null)}
            >
                <ArrowLeftIcon style={{ width: 16, height: 16, color: "#264164" }} />
                <span className="text-sm font-semibold" style={{ color: "#264164" }}>Back</span>
                <span className="text-sm" style={{ color: "#6b7280" }}>— {selectedActiveCity} ({activeCityUsersTotal.toLocaleString()})</span>
            </div>
            <div style={{ flexShrink: 0, padding: "8px 0 0" }}>
                <PrimeInputText
                    value={activeCitySearch}
                    onChange={(e) => handleActiveCitySearchChange(e.target.value)}
                    placeholder="Search by name or mobile..."
                    className="w-full"
                    style={{ fontSize: "13px" }}
                />
            </div>
            <div onScroll={handleActiveCityUsersScroll} style={{ flex: 1, overflowY: "auto", paddingTop: "8px" }}>
                {activeCityUsers.length === 0 && !activeCityUsersLoading ? (
                    <p className="text-sm m-0" style={{ color: "#9ca3af", textAlign: "center", padding: "24px 0" }}>
                        No users found
                    </p>
                ) : (
                    activeCityUsers.map((u: any) => (
                        <div
                            key={u.id}
                            className="flex items-center gap-2 cursor-pointer"
                            style={{ padding: "6px 0", borderBottom: "1px solid #e5e7eb" }}
                            onClick={() => navigate(`/admin/profiles/view/${u.id}`)}
                        >
                            <img
                                src={u.profileimg || `${process.env.REACT_APP_API_URL}/public/userprofile/default.png`}
                                alt={u.name}
                                style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: "#f3f4f6" }}
                            />
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <p className="m-0 text-xs font-medium" style={{ color: "#264164", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {u.name} {u.surname}
                                </p>
                                <p className="m-0" style={{ color: "#6b7280", fontSize: "11px" }}>{u.mobileno}</p>
                            </div>
                        </div>
                    ))
                )}
                {activeCityUsersLoading && (
                    <div className="flex flex-col gap-2" style={{ padding: "8px 0" }}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} height="2rem" className="rounded" />
                        ))}
                    </div>
                )}
            </div>
        </div>
    ) : null;

    // Site-wide (all screens) active-users-last-30-min — same number shown
    // on the Dashboard, kept separate from the screen-scoped map data above.
    const [siteWideActive30Min, setSiteWideActive30Min] = useState<number | null>(null);

    const [showMessageDialog, setShowMessageDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

    const [sendType, setSendType] = useState<"instant" | "schedule">("instant");
    const [scheduleType, setScheduleType] = useState<string>("once");
    const [scheduledDate, setScheduledDate] = useState<any>(null);
    const [scheduledTime, setScheduledTime] = useState<string>("");
    const [monthDate, setMonthDate] = useState<number>(1);
    const [specificDays, setSpecificDays] = useState<string[]>([]);
    const [dayTimes, setDayTimes] = useState<{ day: string; time: string }[]>([]);

    const [selectAllUsers, setSelectAllUsers] = useState(false);

    const [waTemplate, setWaTemplate] = useState("");
    const [waParams, setWaParams] = useState<string[]>([]);
    const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

    const [linkType, setLinkType] = useState<"none" | "post" | "profile" | "chat">("none");
    const [linkValue, setLinkValue] = useState("");
    const [waConfirmVisible, setWaConfirmVisible] = useState(false);
    const [waRecipientCount, setWaRecipientCount] = useState<number | null>(null);
    const [waPendingPayload, setWaPendingPayload] = useState<any>(null);

    const [refreshing, setRefreshing] = useState(false);

    const sendTypeOptions = [
        { label: "Send Now", value: "instant" },
        { label: "Schedule", value: "schedule" }
    ];

    const scheduleTypeOptions = [
        { label: "One Time", value: "once" },
        { label: "Daily", value: "daily" },
        { label: "Weekly", value: "weekly" },
        { label: "Monthly", value: "monthly" },
        { label: "Specific Days", value: "specific_days" },
        { label: "Specific Days & Times", value: "day_time" }
    ];

    const weekDays = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday"
    ];

    const startIndex = (currentPage - 1) * itemsPerPage;

    const pageTitle = `Active Users - ${location.state?.title}` || "Active Users";

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const getCityChartData = useCallback(async () => {
        try {
            const response = await apiFunctions.get("/activeusers/citychart");
            if (response?.data?.success) {
                setCityChartData(response.data.data || []);
            }
        } catch (err) {
            console.error("Failed to load city chart:", err);
        }
    }, []);

    const getHourlyChartData = useCallback(async () => {
        try {
            const response = await apiFunctions.get("/activeusers/hourlychart", {
                params: screen ? { screen } : {},
            });
            if (response?.data?.success) {
                setHourlyChartData(response.data.data || []);
            }
        } catch (err) {
            console.error("Failed to load hourly chart:", err);
        }
    }, [screen]);

    // City-wise chart data — fetched once
    useEffect(() => {
        getCityChartData();
    }, [getCityChartData]);

    // Hour-wise activity chart data — refetched when screen changes
    useEffect(() => {
        getHourlyChartData();
    }, [getHourlyChartData]);

    const getActiveLocations = useCallback(async () => {
        try {
            setActiveLocationsLoading(true);
            const response = await apiFunctions.get("/activeusers/locations", {
                params: { ...(screen ? { screen } : {}), range: locationRange },
            });
            if (response?.data?.success) {
                setActiveLocations(response.data.locations || []);
                setTotalActiveLocationUsers(response.data.totalActiveUsers || 0);
            }
        } catch (err) {
            console.error("Failed to load active user locations:", err);
        } finally {
            setActiveLocationsLoading(false);
        }
    }, [screen, locationRange]);

    // Active users by location (today, or last 30 minutes) — refetched when
    // screen or the selected range changes
    useEffect(() => {
        getActiveLocations();
    }, [getActiveLocations]);

    // Site-wide active users (last 30 min) — same number as the Dashboard, fetched once
    useEffect(() => {
        (async () => {
            try {
                const response = await apiFunctions.get("/activeusers/summary");
                if (response?.data?.success) {
                    setSiteWideActive30Min(response.data.last30Min ?? 0);
                }
            } catch (err) {
                console.error("Failed to load site-wide active users summary:", err);
            }
        })();
    }, []);

    // Render hourly activity bar chart
    useEffect(() => {
        if (!hourlyCanvasRef.current || hourlyChartData.length === 0) return;

        const hourLabel = (h: number) =>
            h === 0 ? "12a" : h < 12 ? `${h}a` : h === 12 ? "12p" : `${h - 12}p`;

        // full 24-hour series, missing hours = 0
        const series = Array.from({ length: 24 }, (_, h) => ({
            label: hourLabel(h),
            count: hourlyChartData.find(d => d.hour === h)?.count || 0,
        }));

        hourlyChartRef.current?.destroy();
        hourlyChartRef.current = new Chart(hourlyCanvasRef.current, {
            type: "bar",
            data: {
                labels: series.map(s => s.label),
                datasets: [{
                    label: "Active Users",
                    data: series.map(s => s.count),
                    backgroundColor: "#2a8f9e",
                    borderRadius: 4,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                    y: { beginAtZero: true, ticks: { precision: 0, color: "#374151", font: { weight: "bold" as const } } },
                    x: { grid: { display: false }, ticks: { color: "#374151", font: { weight: "bold" as const } } },
                },
            },
        });

        return () => {
            hourlyChartRef.current?.destroy();
            hourlyChartRef.current = null;
        };
    }, [hourlyChartData]);

    // Render grouped bar chart (total vs active per city)
    useEffect(() => {
        if (!cityCanvasRef.current || cityChartData.length === 0) return;

        cityChartRef.current?.destroy();
        cityChartRef.current = new Chart(cityCanvasRef.current, {
            type: "bar",
            data: {
                labels: cityChartData.map(c => c.city),
                datasets: [
                    {
                        label: "Total Users",
                        data: cityChartData.map(c => c.total),
                        backgroundColor: "#2a8f9e",
                        borderRadius: 4,
                    },
                    {
                        label: "Active (Today)",
                        data: cityChartData.map(c => c.active),
                        backgroundColor: "#264164",
                        borderRadius: 4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: "top", labels: { color: "#374151", font: { weight: "bold" as const } } },
                },
                scales: {
                    y: { beginAtZero: true, ticks: { precision: 0, color: "#374151", font: { weight: "bold" as const } } },
                    x: { grid: { display: false }, ticks: { color: "#374151", font: { weight: "bold" as const } } },
                },
            },
        });

        return () => {
            cityChartRef.current?.destroy();
            cityChartRef.current = null;
        };
    }, [cityChartData]);

    const actionBodyTemplate = (rowData: BazarProduct) => {
        return (
            <div className="flex items-center gap-2">
                <Button label="View" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleEdit(rowData)} />
            </div>
        );
    };

    const openBulkMessageDialog = (sendToAll = false) => {
        setSelectAllUsers(sendToAll);

        setSelectedUser(null);

        if (sendToAll) {
            setSelectedUsers([]);
        }

        setTitle("Sagar Samaj");
        setMessage("");

        setSendType("instant");
        setScheduleType("once");
        setScheduledDate(null);
        setScheduledTime("");
        setMonthDate(1);
        setSpecificDays([]);
        setDayTimes([]);

        setLinkType("none");
        setLinkValue("");

        setShowMessageDialog(true);
    };

    const sendMessage = async () => {

        if (!title.trim()) {
            showToast({
                severity: "warn",
                summary: "Warning",
                detail: "Please enter a title",
                life: 3000
            });
            return;
        }

        if (!message.trim()) {
            showToast({
                severity: "warn",
                summary: "Warning",
                detail: "Please enter a message",
                life: 3000
            });
            return;
        }

        // Collect userIds
        let userIds: string[] = [];

        // Single user
        if (selectedUser?.userid) {
            userIds = [selectedUser.userid];
        }

        // Multiple users
        if (selectedUsers.length > 0) {
            userIds = selectedUsers.map(u => u.userid);
        }

        const isSendToAll =
            !selectedUser && selectedUsers.length === 0;

        if (!userIds.length && !isSendToAll) {
            showToast({
                severity: "error",
                summary: "Error",
                detail: "No users selected",
                life: 3000
            });
            return;
        }

        try {
            setSending(true);

            const payload = {
                userIds,
                title,
                message,
                send_type: sendType,
                target_type: selectAllUsers
                    ? "all_users"
                    : "selected_users",
                schedule_type: sendType === "schedule" ? scheduleType : "",
                scheduled_date: scheduledDate ? new Date(scheduledDate) : null,
                scheduled_time: scheduledTime,
                month_date: monthDate,
                specific_days: specificDays,
                day_times: dayTimes,
                whatsapp_template: waTemplate,
                whatsapp_params: waParams,
                link_type: linkType,
                link_value: linkValue.trim()
            };

            const response = await apiFunctions.post(
                "/activeusers/notifications",
                payload
            );

            if (response.data.success) {

                showToast({
                    severity: "success",
                    summary: "Success",
                    detail: sendType === "instant"
                        ? "Notification started in background"
                        : "Notification scheduled successfully",
                    life: 4000
                });

                setShowMessageDialog(false);
                setTitle("");
                setMessage("");
                setSelectedUser(null);
                setSelectedUsers([]);
                setSelectAllUsers(false);
                setWaTemplate("");
                setWaParams([]);
                setLinkType("none");
                setLinkValue("");
            }

        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSending(false);
        }
    };

    // Step 1: validate + figure out the recipient count, then show a
    // confirmation dialog before anything actually gets sent.
    const sendWhatsAppOnly = async () => {

        if (!waTemplate) {
            showToast({
                severity: "warn",
                summary: "Warning",
                detail: "Please select a WhatsApp template",
                life: 3000
            });
            return;
        }

        let userIds: string[] = [];

        if (selectedUser?.userid) {
            userIds = [selectedUser.userid];
        }

        if (selectedUsers.length > 0) {
            userIds = selectedUsers.map(u => u.userid);
        }

        if (!userIds.length && !selectAllUsers) {
            showToast({
                severity: "error",
                summary: "Error",
                detail: "No users selected",
                life: 3000
            });
            return;
        }

        setWaPendingPayload({
            userIds,
            target_type: selectAllUsers ? "all_users" : "selected_users",
            template: waTemplate,
            params: waParams
        });

        if (selectAllUsers) {
            setWaRecipientCount(null);
            setWaConfirmVisible(true);
            try {
                const countRes = await apiFunctions.get("/activeusers/whatsapp-all-users-count");
                if (countRes.data.success) {
                    setWaRecipientCount(countRes.data.count);
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            }
        } else {
            setWaRecipientCount(userIds.length);
            setWaConfirmVisible(true);
        }
    };

    // Step 2: fires only after the admin confirms the recipient count
    const confirmSendWhatsApp = async () => {
        if (!waPendingPayload) return;

        try {
            setSendingWhatsApp(true);

            const response = await apiFunctions.post(
                "/activeusers/whatsapp-notifications",
                waPendingPayload
            );

            if (response.data.success) {

                if (response.data.summary) {
                    const { total, sent, failed } = response.data.summary;
                    showToast({
                        severity: failed > 0 ? "warn" : "success",
                        summary: "WhatsApp Report",
                        detail: `Total: ${total}, Sent: ${sent}, Failed: ${failed}`,
                        life: 5000
                    });
                } else {
                    showToast({
                        severity: "info",
                        summary: "WhatsApp Started",
                        detail: "WhatsApp sending is running in background",
                        life: 4000
                    });
                }

                setShowMessageDialog(false);
                setWaTemplate("");
                setWaParams([]);
                setWaConfirmVisible(false);
                setWaPendingPayload(null);
            }

        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSendingWhatsApp(false);
        }
    };

    const handleRefreshUsers = async () => {
        try {
            setRefreshing(true);
            await Promise.all([getNewLeadsData(), getCityChartData(), getHourlyChartData(), getActiveLocations()]);

            showToast({
                severity: "success",
                summary: "Refreshed",
                detail: "Latest active users loaded",
                life: 2000
            });
        } catch (err) {
            console.log(err);
        } finally {
            setRefreshing(false);
        }
    };

    async function getNewLeadsData() {
        try {
            setDataLoad(true);
            const page = currentPage + 1;

            const params: any = {
                page,
                limit: itemsPerPage,
                sortField,
                sortOrder: sortOrder === 1 ? "asc" : "desc",
            };

            if (screen) params.screen = screen;
            if (name) params.search = name;
            if (activeAgo) params.activeAgo = activeAgo;

            const response = await apiFunctions.get("/activeusers", { params });

            if (response.data.success) {
                setNewLeadsData(response.data.data);
                settotalRecords(response.data.totalRecords);
                settotalPages(response.data.totalPages);
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response.data.message,
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setDataLoad(false);
            setLoad(false);
        }
    }

    const handleEdit = (rowData: any) => {
        navigate(`/admin/profiles/view/${rowData.userid}`);
    };

    const onSort = (e: any) => {
        setSortOrder(e.sortOrder);
        setSortField(e.sortField);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const getsearchdata = (e: any) => {
        if (e.target.value.length >= 2) {
            setName(e.target.value);
            setCurrentPage(0);
        } else {
            setName("");
        }
    };

    const handleActiveAgoChange = (value: string) => {
        setActiveAgo(value);
        setCurrentPage(0);
    };

    useEffect(() => {
        setSkipData(currentPage * itemsPerPage);
    }, [currentPage, itemsPerPage]);

    useEffect(() => {
        if (!load) {
            getNewLeadsData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipData, itemsPerPage, sortField, sortOrder, name, activeAgo]);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getNewLeadsData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const header = (
        <div className="grid grid-cols-12 gap-2 items-start sm:items-center table-header">

            {/* Left: Title + Report */}
            <div className="col-span-12 sm:col-span-7 flex flex-col gap-1">

                {/* Page Title */}
                <h2 className="card-title m-0 text-lg sm:text-xl">
                    {pageTitle}
                </h2>

            </div>

            {/* Right: Actions */}
            <div className="col-span-12 sm:col-span-5 flex flex-wrap items-center justify-start sm:justify-end gap-2">

                <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-gray-600">Search:</span>
                    <PrimeInputText
                        type="text"
                        className="search-input w-full sm:w-auto"
                        placeholder="Search by name, mobile or city..."
                        onChange={(e) => getsearchdata(e)}
                    />
                </div>

                <Dropdown
                    value={activeAgo}
                    options={activeAgoOptions}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => handleActiveAgoChange(e.value)}
                    placeholder="Active Ago"
                    className="w-full sm:w-auto"
                />

                <Button
                    icon={<RefreshCw size={20} strokeWidth={2.2} />}
                    className="w-11 h-11 flex items-center justify-center flex-shrink-0"
                    severity="info"
                    outlined
                    rounded
                    loading={refreshing}
                    onClick={handleRefreshUsers}
                    title="Refresh Active Users"
                />

                <div className="flex gap-2 w-full sm:w-auto flex-1 sm:flex-none" style={{ minWidth: 0 }}>
                    <Button
                        label="Send All"
                        icon={<Users size={20} strokeWidth={2.2} />}
                        className="gap-2 flex items-center justify-center flex-1 sm:flex-none"
                        severity="help"
                        onClick={() => openBulkMessageDialog(true)}
                    />

                    {selectedUsers.length > 0 && (
                        <Button
                            label={`Send (${selectedUsers.length})`}
                            icon={<BellIcon size={20} strokeWidth={2.2} />}
                            className="gap-2 flex items-center justify-center flex-1 sm:flex-none"
                            severity="success"
                            onClick={() => openBulkMessageDialog(false)}
                        />
                    )}
                </div>

            </div>

        </div>
    );

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text mx-1">entries</span>;
        },
    };

    return (
        <>
            <div className="dashboard-margin">
                <div className="bg-white p-4 mb-4" style={{ borderRadius: "8px", borderLeft: "4px solid #22c55e" }}>
                    <p className="text-xs m-0" style={{ color: "#22c55e", fontWeight: 700, letterSpacing: "0.5px" }}>● LIVE</p>
                    <p className="text-xs m-0 mt-1" style={{ color: "#6b7280" }}>Active Users (Last 30 Minutes) — All Screens</p>
                    <span style={{ fontSize: "28px", fontWeight: 700, color: "#264164" }}>
                        {siteWideActive30Min != null ? siteWideActive30Min.toLocaleString() : "—"}
                    </span>
                </div>
                {cityChartData.length > 0 && (
                    <div className="bg-white p-4 mb-4" style={{ borderRadius: "8px" }}>
                        <h3 className="m-0 mb-1" style={{ fontSize: "16px", fontWeight: 700, color: "#264164" }}>
                            City Wise Users
                        </h3>
                        <p className="text-xs m-0 mb-3" style={{ color: "#9ca3af" }}>
                            Total users vs users active in the last 7 days (top 15 cities)
                        </p>
                        <div style={{ height: "320px" }}>
                            <canvas ref={cityCanvasRef} />
                        </div>
                    </div>
                )}
                {hourlyChartData.length > 0 && (
                    <div className="bg-white p-4 mb-4" style={{ borderRadius: "8px" }}>
                        <h3 className="m-0 mb-1" style={{ fontSize: "16px", fontWeight: 700, color: "#264164" }}>
                            Hourly Activity{screen ? ` — ${screen}` : ""}
                        </h3>
                        <p className="text-xs m-0 mb-3" style={{ color: "#9ca3af" }}>
                            {screen
                                ? `Which hours users are most active on "${screen}" screen (last 24 hours)`
                                : "Which hours users are most active (last 24 hours)"}
                        </p>
                        <div style={{ height: "280px" }}>
                            <canvas ref={hourlyCanvasRef} />
                        </div>
                    </div>
                )}
                <div className="mb-4">
                    <div className="flex items-center justify-end gap-2 mb-2">
                        <span className="text-xs" style={{ color: "#6b7280" }}>Range:</span>
                        <Dropdown
                            value={locationRange}
                            options={locationRangeOptions}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setLocationRange(e.value)}
                            className="w-full sm:w-auto"
                        />
                    </div>
                    <LocationMap
                        title={`Active Users by Location${screen ? ` — ${screen}` : ""}`}
                        loading={activeLocationsLoading}
                        locations={activeLocations}
                        subtitle={`${totalActiveLocationUsers.toLocaleString()} active user${totalActiveLocationUsers === 1 ? "" : "s"} (${locationRange === "today" ? "today" : "last 30 minutes"}) across ${activeLocations.length} mapped cities`}
                        unitLabel="active user"
                        autoFit={false}
                        onCityClick={handleActiveCityClick}
                        citySidebarContent={activeCitySidebarContent}
                    />
                </div>
                {isMobile ? (
                    <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                        <div className="p-3 pb-2">{header}</div>
                        {!dataLoad && newLeadsData.length > 0 && (
                            <div className="flex items-center gap-2 px-3 pb-2">
                                <Checkbox
                                    inputId="selectAllOnPage"
                                    checked={
                                        newLeadsData.length > 0 &&
                                        newLeadsData.every((u: any) => selectedUsers.some((su) => su.userid === u.userid))
                                    }
                                    onChange={(e) => {
                                        if (e.checked) {
                                            setSelectedUsers((prev) => {
                                                const merged = [...prev];
                                                newLeadsData.forEach((u: any) => {
                                                    if (!merged.some((su) => su.userid === u.userid)) merged.push(u);
                                                });
                                                return merged;
                                            });
                                        } else {
                                            setSelectedUsers((prev) => prev.filter((u) => !newLeadsData.some((n: any) => n.userid === u.userid)));
                                        }
                                    }}
                                />
                                <label htmlFor="selectAllOnPage" className="text-xs cursor-pointer" style={{ color: "#6b7280" }}>Select all on this page</label>
                            </div>
                        )}
                        <div className="flex flex-col gap-2 px-3 pb-3">
                            {dataLoad ? (
                                Array.from({ length: itemsPerPage }, (_, i) => (
                                    <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <Skeleton width="60%" height="1rem" className="mb-2" />
                                        <Skeleton width="40%" height="0.8rem" />
                                    </div>
                                ))
                            ) : newLeadsData.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "64px 0" }}>
                                    <UserIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                    <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No active users found</p>
                                </div>
                            ) : (
                                newLeadsData.map((rowData: any) => {
                                    const isSelected = selectedUsers.some((u) => u.userid === rowData.userid);
                                    return (
                                        <div
                                            key={rowData.userid}
                                            className="border rounded-lg p-3 w-full box-border"
                                            style={{ borderColor: "#e5e7eb" }}
                                        >
                                            <div className="flex items-start gap-2 w-full">
                                                <Checkbox
                                                    className="mt-1 flex-shrink-0"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        if (e.checked) {
                                                            setSelectedUsers((prev) => [...prev, rowData]);
                                                        } else {
                                                            setSelectedUsers((prev) => prev.filter((u) => u.userid !== rowData.userid));
                                                        }
                                                    }}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>
                                                        {rowData.fullName}
                                                    </p>
                                                    <p className="m-0 text-xs break-words" style={{ color: "#9ca3af" }}>
                                                        {rowData.districtName}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                                <div><span className="font-medium">Active Ago:</span> {rowData.lastActiveAgo}{rowData.city ? ` · ${rowData.city}` : ""}</div>
                                                <div className="break-words"><span className="font-medium">First Active:</span> {formatDateTime(rowData.firstActiveAt)}</div>
                                                <div className="break-words"><span className="font-medium">Last Active:</span> {formatDateTime(rowData.lastActiveAt)}</div>
                                            </div>
                                            <div className="flex justify-end mt-2">
                                                <Button
                                                    label="View"
                                                    outlined
                                                    size="small"
                                                    style={{ color: "#264164", borderColor: "#264164" }}
                                                    onClick={() => handleEdit(rowData)}
                                                />
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                ) : (
                    <DataTable
                        lazy
                        scrollable
                        scrollHeight="calc(100vh - 240px)"
                        style={{ minHeight: "calc(100vh - 165px)", background: "#fff" }}
                        header={header}
                        value={
                            dataLoad
                                ? Array.from({ length: itemsPerPage }, (_, i) => ({ _skeletonId: i }))
                                : newLeadsData
                        }

                        selection={dataLoad ? [] : selectedUsers}
                        onSelectionChange={(e) => !dataLoad && setSelectedUsers(e.value)}
                        dataKey={dataLoad ? "_skeletonId" : "userid"}   // ✅ Important for multi-page select

                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={onSort}
                        resizableColumns
                        removableSort
                        rows={itemsPerPage}
                        emptyMessage={
                            <div style={{ textAlign: "center", padding: "64px 0" }}>
                                <UserIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No active users found</p>
                            </div>
                        }
                    >
                        {dataLoad ? (
                            <Column headerStyle={{ width: "3rem" }} body={() => <Skeleton width="1.2rem" height="1.2rem" />} />
                        ) : (
                            <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
                        )}
                        <Column field="fullName" header="Name" sortable body={(rowData) => dataLoad ? <Skeleton /> : rowData.fullName} />
                        <Column field="lastActiveAgo" header="Active Ago" sortable body={(rowData) => dataLoad ? <Skeleton /> : (
                            <>
                                {rowData.lastActiveAgo}
                                {rowData.city && <div className="text-xs" style={{ color: "#9ca3af" }}>{rowData.city}</div>}
                            </>
                        )} />
                        <Column field="firstActiveAt" header="First Active Date Time" sortable
                            body={(rowData) =>
                                dataLoad ? <Skeleton /> : formatDateTime(rowData.firstActiveAt)
                            }
                        />
                        <Column field="lastActiveAt" header="Last Active Date Time" sortable
                            body={(rowData) =>
                                dataLoad ? <Skeleton /> : formatDateTime(rowData.lastActiveAt)
                            }
                        />
                        <Column field="districtName" header="District" sortable body={(rowData) => dataLoad ? <Skeleton /> : rowData.districtName} />
                        <Column header="Action" className="action-column" frozen body={(rowData) => dataLoad ? <Skeleton /> : actionBodyTemplate(rowData)} />
                    </DataTable>
                )}
                {totalRecords > 0 && <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                    {/* PrimeReact Paginator */}
                    <Paginator
                        template={paginatoreTemplate}
                        first={startIndex}
                        rows={itemsPerPage}
                        totalRecords={totalRecords}
                    />

                    {/* Custom Pagination Buttons */}
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage === 0}
                        >
                            <span className="sm:hidden">Prev</span>
                            <span className="hidden sm:inline">Previous</span>
                        </Button>

                        <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                            <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                Page {currentPage + 1} of {totalPages || 1}
                            </span>
                        </Button>

                        <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                        >
                            Next
                        </Button>
                    </div>
                </div>}
            </div>
            {/* Message Dialog */}
            <Dialog
                header="Send Notification"
                visible={showMessageDialog}
                style={{ width: "90vw", maxWidth: "500px" }}
                modal
                draggable={false}
                onHide={() => setShowMessageDialog(false)}
            >

                <div className="flex flex-col gap-3">

                    <div className="font-semibold text-sm">
                        To: {
                            selectAllUsers
                                ? "All Users"
                                : selectedUser
                                    ? selectedUser.fullName
                                    : `${selectedUsers.length} Users`
                        }
                    </div>

                    {/* Title */}
                    <InputTextarea
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        rows={2}
                        placeholder="Enter notification title..."
                        className="w-full"
                    />

                    {/* Message */}
                    <InputTextarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        placeholder="Enter notification message..."
                        className="w-full"
                    />

                    <NotificationLinkPicker
                        linkType={linkType}
                        linkValue={linkValue}
                        onLinkTypeChange={setLinkType}
                        onLinkValueChange={setLinkValue}
                    />

                    {/* Send Type */}
                    <div>
                        <label className="text-sm font-medium mb-1 block">Send Type</label>
                        <Dropdown
                            value={sendType}
                            options={sendTypeOptions}
                            onChange={(e) => setSendType(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            className="w-full"
                        />
                    </div>

                    {/* Schedule Config */}
                    {sendType === "schedule" && (
                        <>
                            <div>
                                <label className="text-sm font-medium mb-1 block">Schedule Type</label>
                                <Dropdown
                                    value={scheduleType}
                                    options={scheduleTypeOptions}
                                    onChange={(e) => setScheduleType(e.value)}
                                    optionLabel="label"
                                    optionValue="value"
                                    className="w-full"
                                />
                            </div>

                            {scheduleType === "once" && (
                                <Calendar
                                    value={scheduledDate}
                                    onChange={(e) => setScheduledDate(e.value)}
                                    showTime
                                    hourFormat="24"
                                    placeholder="Select Date & Time"
                                    className="w-full"
                                />
                            )}

                            {(scheduleType === "daily" ||
                                scheduleType === "weekly" ||
                                scheduleType === "monthly" ||
                                scheduleType === "specific_days") && (
                                    <input
                                        type="time"
                                        value={scheduledTime}
                                        onChange={(e) => setScheduledTime(e.target.value)}
                                        className="p-2 border rounded w-full"
                                    />
                                )}

                            {scheduleType === "monthly" && (
                                <PrimeInputText
                                    value={monthDate.toString()}
                                    onChange={(e: any) => {
                                        let val = parseInt(e.target.value || "1");

                                        if (isNaN(val)) val = 1;
                                        if (val < 1) val = 1;
                                        if (val > 31) val = 31;

                                        setMonthDate(val);
                                    }}
                                    placeholder="Enter month date (1-31)"
                                    className="w-full"
                                />
                            )}

                            {scheduleType === "specific_days" && (
                                <MultiSelect
                                    value={specificDays}
                                    options={weekDays.map(day => ({ label: day, value: day }))}
                                    onChange={(e) => setSpecificDays(e.value)}
                                    placeholder="Select specific days"
                                    className="w-full"
                                />
                            )}

                            {scheduleType === "day_time" && (
                                <div className="flex flex-col gap-2 border rounded p-3">
                                    <label className="text-sm font-medium">Select days and set time for each</label>
                                    {weekDays.map(day => {
                                        const entry = dayTimes.find(d => d.day === day);
                                        return (
                                            <div key={day} className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={!!entry}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setDayTimes(prev => [...prev, { day, time: "09:00" }]);
                                                        } else {
                                                            setDayTimes(prev => prev.filter(d => d.day !== day));
                                                        }
                                                    }}
                                                />
                                                <span className="capitalize text-sm" style={{ width: "90px" }}>{day}</span>
                                                {entry && (
                                                    <input
                                                        type="time"
                                                        value={entry.time}
                                                        onChange={(e) =>
                                                            setDayTimes(prev => prev.map(d =>
                                                                d.day === day ? { ...d, time: e.target.value } : d
                                                            ))
                                                        }
                                                        className="p-1 border rounded"
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    <WhatsAppTemplateSection
                        template={waTemplate}
                        params={waParams}
                        onTemplateChange={setWaTemplate}
                        onParamsChange={setWaParams}
                    />

                    <div className="flex justify-end gap-2 mt-3 flex-wrap">
                        <Button
                            label="Cancel"
                            severity="secondary"
                            onClick={() => setShowMessageDialog(false)}
                            disabled={sending || sendingWhatsApp}
                        />

                        <Button
                            label="Send WhatsApp"
                            icon={<WhatsAppIcon size={16} />}
                            severity="success"
                            loading={sendingWhatsApp}
                            disabled={!waTemplate || sending}
                            onClick={sendWhatsAppOnly}
                        />

                        <Button
                            label={sendType === "instant" ? "Send Now" : "Schedule"}
                            icon="pi pi-send"
                            loading={sending}
                            disabled={sendingWhatsApp}
                            onClick={sendMessage}
                        />
                    </div>
                </div>
            </Dialog>
            <ConfirmActionPopup
                visible={waConfirmVisible}
                onHide={() => { setWaConfirmVisible(false); setWaPendingPayload(null); }}
                onConfirm={confirmSendWhatsApp}
                header="Confirm WhatsApp Send"
                confirmLabel="Send"
                loading={sendingWhatsApp}
                message={
                    waRecipientCount === null
                        ? "Calculating recipient count..."
                        : `This WhatsApp message will be sent to ${waRecipientCount} user${waRecipientCount === 1 ? "" : "s"}. Are you sure?`
                }
            />
        </>
    );
};

export default ActiveUsersPage;
